import { useEffect, useState, useCallback } from 'react';

interface VersionInfo {
  version: string;
  releaseDate: string;
}

const VERSION_STORAGE_KEY = 'app_last_seen_version';
const POLL_INTERVAL = 5 * 60 * 1000; // 5 minutes

export const useVersionCheck = () => {
  const [hasNewVersion, setHasNewVersion] = useState(false);
  const [currentVersion, setCurrentVersion] = useState<string | null>(null);
  const [lastSeenVersion, setLastSeenVersion] = useState<string | null>(
    () => localStorage.getItem(VERSION_STORAGE_KEY)
  );

  const fetchVersion = useCallback(async () => {
    try {
      // Add cache buster to ensure we get the latest version
      const response = await fetch(`/version.json?t=${Date.now()}`);
      if (!response.ok) {
        console.error('Failed to fetch version info');
        return;
      }

      const versionInfo: VersionInfo = await response.json();
      const latestVersion = versionInfo.version;

      setCurrentVersion(latestVersion);

      // Get the last seen version from localStorage
      const storedVersion = localStorage.getItem(VERSION_STORAGE_KEY);

      // If there's no last seen version, this is the first visit
      // Store current version but don't show notification
      if (!storedVersion) {
        localStorage.setItem(VERSION_STORAGE_KEY, latestVersion);
        setLastSeenVersion(latestVersion);
        return;
      }

      // If versions differ, we have a new version
      if (storedVersion !== latestVersion) {
        setHasNewVersion(true);
      } else {
        // Versions match - no notification needed
        setHasNewVersion(false);
      }
    } catch (error) {
      console.error('Error checking version:', error);
    }
  }, []);

  const dismissNewVersion = useCallback(() => {
    // Only hide the badge, don't sync versions
    // User can still see Update button in modal
    setHasNewVersion(false);
  }, []);

  const applyNewVersion = useCallback(() => {
    if (currentVersion) {
      // Sync versions - user has applied the update
      localStorage.setItem(VERSION_STORAGE_KEY, currentVersion);
      setLastSeenVersion(currentVersion);
      setHasNewVersion(false);
    }
  }, [currentVersion]);

  useEffect(() => {
    // Check on mount
    fetchVersion();

    // Set up polling interval
    const intervalId = setInterval(fetchVersion, POLL_INTERVAL);

    // Cleanup interval on unmount
    return () => clearInterval(intervalId);
  }, [fetchVersion]);

  return {
    hasNewVersion,
    currentVersion,
    lastSeenVersion,
    dismissNewVersion,
    applyNewVersion,
  };
};
